import { useEffect, useState } from 'react'
import api from '../services/api'
import LookupSelect from './LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'

// Endereço do funcionário (TMOTEND do ERP) — colunas da própria frota.cadmot.

// Largura do campo pelo tamanho real da coluna no banco
const largura = (max: number) => Math.max(56, max * 9 + 28)
const LINHA: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }

const fmtCep = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 8)
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d
}

interface Cidade { cid_cod: string; cid_des: string; uf: string; est_des: string; pai_cod: string; pai_des: string }

export default function FuncionarioEnderecoModal({ motcod, onClose }: { motcod: string; onClose: () => void }) {
  const { empresaCod } = useEmpresa()
  const [f, setF] = useState<Record<string, string> | null>(null)
  const [cidade, setCidade] = useState<Cidade | null>(null)
  const [cepNaoAchado, setCepNaoAchado] = useState(false)
  const [obrigatorio, setObrigatorio] = useState(false)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/endereco`)
    const d: Record<string, string> = {}
    for (const [k, v] of Object.entries(r.data)) d[k] = v === null || v === undefined ? '' : String(v)
    d.motcep = d.motcep === '0' ? '' : fmtCep(d.motcep.padStart(8, '0'))
    setF(d)
    setCidade(d.motcidcod && d.motcidcod !== '0'
      ? { cid_cod: d.motcidcod, cid_des: d.cid_des, uf: d.uf, est_des: d.est_des, pai_cod: d.pai_cod, pai_des: d.pai_des }
      : null)
    setCepNaoAchado(false)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar o endereço do funcionário.'))
  }, [motcod])

  useEffect(() => {
    if (!empresaCod) return
    api.get(`/funcionarios/endereco/obrigatorio/${empresaCod}`).then(r => setObrigatorio(!!r.data.obrigatorio)).catch(() => {})
  }, [empresaCod])

  if (!f) {
    return (
      <div className="modal-overlay" style={{ zIndex: 1300 }}>
        <div className="modal-box" style={{ maxWidth: 420, padding: '1.5rem', textAlign: 'center', color: erro ? '#dc2626' : '#6b7280' }}>
          {erro || 'Carregando...'}
          {erro && <div style={{ marginTop: '1rem' }}><button className="btn btn-secondary" onClick={onClose}>Fechar</button></div>}
        </div>
      </div>
    )
  }

  const set = (c: string, v: string) => setF(p => ({ ...p!, [c]: v }))

  // CEP completo → busca a cidade (igual à PCAD003 do ERP)
  async function buscarCep(valor: string) {
    const cep = valor.replace(/\D/g, '')
    if (cep.length !== 8) return
    try {
      const r = await api.get(`/funcionarios/endereco/cep/${cep}`)
      setCidade(r.data)
      set('motcidcod', String(r.data.cid_cod))
      setCepNaoAchado(false)
    } catch {
      setCepNaoAchado(true)
    }
  }

  async function salvar() {
    setErro(''); setAviso('')
    if (!f!.motcep.replace(/\D/g, '')) return setErro('Informe CEP válido!')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/endereco`, { ...f, empresa_logada: empresaCod })
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Endereço salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o endereço.')
    } finally {
      setSalvando(false)
    }
  }

  // Render helpers (funções, não componentes, pra não perder o foco ao digitar)
  const campo = (w: number, label: string, conteudo: React.ReactNode) => (
    <div className="field-cad" style={{ flex: `0 0 ${w}px` }}>
      <label style={{ whiteSpace: 'nowrap' }}>{label}</label>
      {conteudo}
    </div>
  )
  const txt = (c: string, label: string, max: number, opts: { maiusculo?: boolean } = {}) =>
    campo(Math.max(largura(max), label.length * 7 + 10), label,
      <input value={f[c] ?? ''} maxLength={max} style={{ width: '100%', boxSizing: 'border-box' }}
        onChange={e => set(c, opts.maiusculo === false ? e.target.value : e.target.value.toUpperCase())} />)
  const lerSo = (label: string, valor: string, w: number) =>
    campo(w, label, <input value={valor} disabled style={{ width: '100%', boxSizing: 'border-box' }} />)

  const obrig = obrigatorio ? ' *' : ''

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Endereço — {f.motcod} · {f.motnome}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTEND</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          <div style={LINHA}>
            {txt('motende', `Endereço${obrig}`, 40)}
            {txt('motendnro', `Nº${obrig}`, 10)}
            {txt('motbairro', 'Bairro', 20)}
          </div>

          <div style={{ ...LINHA, marginTop: '0.75rem' }}>
            {campo(110, 'CEP *',
              <input value={f.motcep} inputMode="numeric" maxLength={9} style={{ width: '100%', boxSizing: 'border-box' }}
                onChange={e => { const v = fmtCep(e.target.value); set('motcep', v); buscarCep(v) }} />)}
            {cepNaoAchado ? (
              <div className="field-cad" style={{ flex: '0 0 260px' }}>
                <label>Cidade (CEP não encontrado)</label>
                <LookupSelect
                  endpoint="/erp/cidades"
                  codKey="cid_cod"
                  descKey="cid_des"
                  tagKey="cid_estcod"
                  codValue={f.motcidcod}
                  descValue={cidade ? `${cidade.cid_des}${cidade.uf ? ' - ' + cidade.uf : ''}` : ''}
                  onSelect={(cod, desc, item) => {
                    set('motcidcod', cod)
                    setCidade({ cid_cod: cod, cid_des: desc, uf: item?.cid_estcod ?? '', est_des: '', pai_cod: '', pai_des: '' })
                  }}
                  placeholder="Digite para buscar..."
                  somenteComTexto
                  listWidth={300}
                />
              </div>
            ) : (
              lerSo('Cidade', cidade ? `${cidade.cid_cod} · ${cidade.cid_des}` : '', 230)
            )}
            {lerSo('Estado', cidade?.uf ? `${cidade.uf}${cidade.est_des ? ' · ' + cidade.est_des : ''}` : '', 190)}
            {lerSo('País', cidade?.pai_des ?? '', 120)}
          </div>

          <div style={{ ...LINHA, marginTop: '0.75rem' }}>
            {txt('motptoref', 'P. de Referência', 40)}
          </div>

          <div style={{ ...LINHA, marginTop: '0.75rem' }}>
            {txt('motfone', 'Telefone', 14)}
            {txt('motfoncel', 'Celular', 14)}
            {txt('motemail', 'E-mail', 50, { maiusculo: false })}
          </div>

          {erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
